import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building,
  Bell,
  Menu,
  X,
  Search,
  ChevronRight,
  ChevronDown,
  User,
  LogOut,
  LayoutDashboard,
  MonitorPlay,
  CreditCard,
  Settings,
  Crown,
  CheckCircle2,
  Lock,
  Image as ImageIcon,
  Loader2,
  Camera,
  ShieldCheck,
  Smartphone,
  Trash2,
  AlertCircle,
  AlertTriangle,
  Info,
  HelpCircle,
  Briefcase,
  CheckCheck,
  MessageCircle,
  Globe,
  Zap
} from 'lucide-react';
import API_BASE_URL from '../../../../config';
import BrandLogo from './BrandLogo';

const SEARCH_ITEMS = [
  { title: 'Home', description: 'Go to main dashboard & overview', tabId: 'home', category: 'Page' },
  { title: 'About', description: 'Learn about our mission, values & clients', tabId: 'aboutus', category: 'Page' },
  { title: 'Solutions', description: 'Explore high-performance computing, engineering & AI solutions', tabId: 'solutions', category: 'Page' },
  { title: 'Lab Access', description: 'Access remote labs, VMs, and choose subscription plans', tabId: 'lab', category: 'Page' },
  { title: 'Contact Us', description: 'Get in touch with support, sales, or careers team', tabId: 'contact', category: 'Page' },
  { title: 'Careers', description: 'Join MP-X Global Solutions, explore job openings', tabId: 'careers', category: 'Page' },
  { title: 'Help Center', description: 'Comprehensive guide, setup tutorials, network configs', tabId: 'helpcenter', category: 'Support' },
  { title: 'FAQ', description: 'Frequently asked questions about remote labs and billing', tabId: 'faq', category: 'Support' },
  { title: 'Troubleshoot', description: 'Resolve connection issues, RDS configs, and optimization', tabId: 'troubleshoot', category: 'Support' },
  { title: 'Demo Lab', description: 'Test remote lab configurations and performance', tabId: 'demolab', category: 'Lab' },
  { title: 'Dashboard', description: 'Access your active labs and VM controls', tabId: 'lab-dashboard', category: 'Lab' },
  { title: 'Engineering Hub', description: 'Explore engineering services, industries, case studies, and thought leadership', tabId: 'engineering-hub', category: 'Page' },
  { title: 'Energy Sourcing', description: 'Certified Testing & Commissioning, Substation (11kV-765kV), Protection & Relay Engineers', tabId: 'manpower-sourcing', category: 'Page' },
  { title: 'Pricing Plans', description: 'View monthly and yearly remote lab pricing options', tabId: 'lab', elementId: 'pricing-section', category: 'Pricing' },
  { title: 'Sites', description: 'Visit external MP-X Sites portal (sites.mp-xglobalsolutions.com)', externalUrl: 'https://sites.mp-xglobalsolutions.com', category: 'Portal' }
];

const LANGUAGES = [
  // Column 1
  { code: 'es', googleCode: 'es', name: 'Español', useGlobe: true },
  { code: 'de', googleCode: 'de', name: 'Deutsch', flagIso: 'de' },
  { code: 'nl', googleCode: 'nl', name: 'Nederlands', flagIso: 'nl' },
  { code: 'ar', googleCode: 'ar', name: 'العربية', useGlobe: true },
  { code: 'th', googleCode: 'th', name: 'ภาษาไทย', flagIso: 'th' },
  { code: 'zh', googleCode: 'zh-CN', name: '简体中文', flagIso: 'cn' },
  { code: 'ta', googleCode: 'ta', name: 'தமிழ்', flagIso: 'in' },

  // Column 2
  { code: 'pt-br', googleCode: 'pt', name: 'Português (Brasil)', flagIso: 'br' },
  { code: 'fr', googleCode: 'fr', name: 'Français', flagIso: 'fr' },
  { code: 'ja', googleCode: 'ja', name: '日本語', flagIso: 'jp' },
  { code: 'id', googleCode: 'id', name: 'Bahasa Indonesia', flagIso: 'id' },
  { code: 'vi', googleCode: 'vi', name: 'Tiếng Việt', flagIso: 'vn' },
  { code: 'hi', googleCode: 'hi', name: 'हिन्दी', flagIso: 'in' },
  { code: 'en', googleCode: 'en', name: 'English', flagIso: 'us' }
];

const Navbar = ({ activeTab, setActiveTab, menuItems, user, setUser, purchasedPlan, setPurchasedPlan }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [selectedSearchCategory, setSelectedSearchCategory] = useState('All');
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState(() => localStorage.getItem('mpx_selected_language') || 'en');
  const [langToast, setLangToast] = useState(null);
  const langDropdownRef = useRef(null);
  const langTimeoutRef = useRef(null);
  const searchModalInputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isSignoutModalOpen, setIsSignoutModalOpen] = useState(false);
  const [passwords, setPasswords] = useState({ old: '', new: '', confirm: '' });
  const [passError, setPassError] = useState('');
  const [passSuccess, setPassSuccess] = useState('');
  const [passLoading, setPassLoading] = useState(false);
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // MFA States
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [mfaStep, setMfaStep] = useState('status'); // 'status', 'select', 'totp-setup', 'email-setup'
  const [mfaSecret, setMfaSecret] = useState('');
  const [mfaQrCode, setMfaQrCode] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaDevices, setMfaDevices] = useState([]);
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [isMfaVerified, setIsMfaVerified] = useState(false);

  // Notification States
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [activeModalNotification, setActiveModalNotification] = useState(null);
  const [notificationFilter, setNotificationFilter] = useState('all'); // 'all' | 'unread' | 'labs' | 'billing'



  const fetchNotifications = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoadingNotifications(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  const markAsRead = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/${id}/read`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        setNotifications(prev =>
          prev.map(notif => notif.id === id ? { ...notif, isRead: true } : notif)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error marking notification read:', err);
    }
  };

  const markAllAsRead = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/read-all`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        setNotifications(prev =>
          prev.map(notif => ({ ...notif, isRead: true }))
        );
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Error marking all notifications read:', err);
    }
  };

  const clearReadNotifications = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const readIds = notifications.filter(n => n.isRead).map(n => n.id);
    if (readIds.length === 0) return;

    setNotifications(prev => prev.filter(n => !n.isRead));

    try {
      await Promise.all(
        readIds.map(id =>
          fetch(`${API_BASE_URL}/api/notifications/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        )
      );
    } catch (err) {
      console.error('Error clearing read notifications:', err);
    }
  };

  const deleteNotification = async (id, e) => {
    if (e) e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const deleted = notifications.find(n => n.id === id);
        setNotifications(prev => prev.filter(notif => notif.id !== id));
        if (deleted && !deleted.isRead) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (isNaN(diffInSeconds) || diffInSeconds < 0) return 'Just now';
    if (diffInSeconds < 30) return 'Just now';
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getNotificationMeta = (notif) => {
    const type = (notif.type || '').toLowerCase();
    const category = (notif.category || '').toLowerCase();
    const title = (notif.title || '').toLowerCase();

    if (type === 'critical' || type === 'error' || title.includes('error') || title.includes('fail') || title.includes('alert')) {
      return {
        icon: AlertCircle,
        iconBg: 'bg-red-50 text-red-500 border border-red-100',
        badgeBg: 'bg-red-100/80 text-red-700 font-bold'
      };
    }
    if (type === 'warning' || title.includes('expir') || title.includes('warn') || title.includes('limit')) {
      return {
        icon: AlertTriangle,
        iconBg: 'bg-amber-50 text-amber-500 border border-amber-100',
        badgeBg: 'bg-amber-100/80 text-amber-700 font-bold'
      };
    }
    if (category === 'billing' || category === 'payment' || title.includes('payment') || title.includes('invoice') || title.includes('receipt') || title.includes('plan')) {
      return {
        icon: CreditCard,
        iconBg: 'bg-emerald-50 text-emerald-600 border border-emerald-100',
        badgeBg: 'bg-emerald-100/80 text-emerald-700 font-bold'
      };
    }
    if (category === 'lab' || category === 'vm' || category === 'server' || title.includes('vm') || title.includes('lab') || title.includes('instance')) {
      return {
        icon: MonitorPlay,
        iconBg: 'bg-blue-50 text-blue-600 border border-blue-100',
        badgeBg: 'bg-blue-100/80 text-blue-700 font-bold'
      };
    }
    if (category === 'security' || category === 'auth' || title.includes('mfa') || title.includes('password') || title.includes('login')) {
      return {
        icon: ShieldCheck,
        iconBg: 'bg-purple-50 text-purple-600 border border-purple-100',
        badgeBg: 'bg-purple-100/80 text-purple-700 font-bold'
      };
    }
    if (category === 'support' || title.includes('ticket') || title.includes('help')) {
      return {
        icon: HelpCircle,
        iconBg: 'bg-indigo-50 text-indigo-600 border border-indigo-100',
        badgeBg: 'bg-indigo-100/80 text-indigo-700 font-bold'
      };
    }
    if (category === 'career' || title.includes('job') || title.includes('career')) {
      return {
        icon: Briefcase,
        iconBg: 'bg-teal-50 text-teal-600 border border-teal-100',
        badgeBg: 'bg-teal-100/80 text-teal-700 font-bold'
      };
    }
    return {
      icon: Bell,
      iconBg: 'bg-slate-100 text-[#3b82f6] border border-slate-200/60',
      badgeBg: 'bg-slate-100 text-slate-700 font-semibold'
    };
  };

  const getNotificationTarget = (notif) => {
    // 1. Explicit target_tab / target_element from backend
    if (notif.target_tab || notif.target_element || notif.element_id) {
      return {
        tabId: notif.target_tab || 'home',
        elementId: notif.target_element || notif.element_id || null,
        label: notif.target_label || `Go to ${notif.target_tab || 'Page'}`
      };
    }

    // 2. Explicit link parsing if backend sent a URI/URL
    if (notif.link) {
      if (notif.link.includes('lab-dashboard')) return { tabId: 'lab-dashboard', elementId: 'active-lab-section', label: 'Open Lab Dashboard' };
      if (notif.link.includes('payment') || notif.link.includes('billing')) return { tabId: 'payment', elementId: 'payment-methods-section', label: 'Go to Billing & Invoices' };
      if (notif.link.includes('pricing') || notif.link.includes('plans')) return { tabId: 'lab', elementId: 'pricing-section', label: 'View Pricing & Plans' };
      if (notif.link.includes('help') || notif.link.includes('faq')) return { tabId: 'helpcenter', elementId: 'faq-section', label: 'View Help FAQ' };
      if (notif.link.includes('troubleshoot')) return { tabId: 'troubleshoot', elementId: 'connection-issues-section', label: 'Troubleshoot Connections' };
      if (notif.link.includes('clients')) return { tabId: 'aboutus', elementId: 'our-clients', label: 'Explore Our Clients' };
      if (notif.link.includes('career')) return { tabId: 'careers', elementId: 'careers-form', label: 'View Career Openings' };
      if (notif.link.includes('contact')) return { tabId: 'contact', elementId: 'reach-out-form', label: 'Contact MP-X Team' };
    }

    const category = (notif.category || '').toLowerCase();
    const title = (notif.title || '').toLowerCase();
    const message = (notif.message || '').toLowerCase();

    // VM / Lab / Server / Instance / Cluster
    if (category === 'lab' || category === 'vm' || category === 'server' || 
        title.includes('vm') || title.includes('lab') || title.includes('instance') || title.includes('cluster') ||
        message.includes('vm') || message.includes('lab') || message.includes('server')) {
      if (title.includes('expir') || title.includes('renew') || title.includes('upgrade') || title.includes('plan')) {
        return { 
          tabId: purchasedPlan ? 'lab-dashboard' : 'lab', 
          elementId: purchasedPlan ? 'dashboard-pricing-section' : 'pricing-section', 
          label: purchasedPlan ? 'Upgrade / Renew Cluster Plan' : 'Explore Subscription Plans' 
        };
      }
      return {
        tabId: purchasedPlan ? 'lab-dashboard' : 'lab',
        elementId: purchasedPlan ? 'active-lab-section' : 'pricing-section',
        label: purchasedPlan ? 'Open Active Lab Session' : 'View Available Labs'
      };
    }

    // Billing / Payment / Invoice / Subscription / Receipt
    if (category === 'billing' || category === 'payment' || category === 'invoice' ||
        title.includes('payment') || title.includes('invoice') || title.includes('bill') || title.includes('plan') || title.includes('subscription') || title.includes('receipt') ||
        message.includes('payment') || message.includes('invoice') || message.includes('receipt')) {
      if (title.includes('plan') || title.includes('renew') || title.includes('pricing')) {
        return { tabId: 'lab', elementId: 'pricing-section', label: 'View Subscription Plans' };
      }
      return { tabId: 'payment', elementId: 'payment-methods-section', label: 'Manage Billing & Invoices' };
    }

    // Security / MFA / Login / Password / Auth
    if (category === 'security' || category === 'auth' || title.includes('mfa') || title.includes('password') || title.includes('login') || title.includes('security')) {
      return { tabId: 'profile_modal', elementId: null, label: 'Open Security & MFA Settings' };
    }

    // Support / Help / FAQ / Troubleshooting
    if (category === 'support' || title.includes('ticket') || title.includes('help') || title.includes('guide') || title.includes('faq')) {
      if (title.includes('troubleshoot') || title.includes('connection') || title.includes('rds') || message.includes('connection')) {
        return { tabId: 'troubleshoot', elementId: 'connection-issues-section', label: 'View Troubleshooting Guide' };
      }
      return { tabId: 'helpcenter', elementId: 'faq-section', label: 'Open Help Center & FAQ' };
    }

    // Careers / Openings / Jobs
    if (category === 'career' || title.includes('career') || title.includes('job') || title.includes('interview')) {
      return { tabId: 'careers', elementId: 'careers-form', label: 'View Careers & Applications' };
    }

    // About
    if (category === 'about' || title.includes('about') || title.includes('company')) {
      return { tabId: 'aboutus', elementId: null, label: 'View About Page' };
    }

    // Contact / Support form
    if (category === 'contact' || title.includes('contact') || title.includes('reach')) {
      return { tabId: 'contact', elementId: 'reach-out-form', label: 'Reach Out to MP-X Team' };
    }

    return { tabId: 'home', elementId: null, label: 'Go to Main Overview' };
  };

  const handleNotificationClick = (notif) => {
    if (!notif.isRead) {
      markAsRead(notif.id);
    }
    setActiveModalNotification(notif);
    setIsNotificationsOpen(false);
  };

  const handleNotificationDirectNavigate = (notif, e) => {
    if (e) e.stopPropagation();
    if (!notif.isRead) {
      markAsRead(notif.id);
    }
    const target = getNotificationTarget(notif);
    if (target) {
      if (target.tabId === 'profile_modal') {
        setIsProfileOpen(true);
      } else if (target.tabId) {
        handleNavClick(target.tabId, target.elementId);
      }
    }
    setIsNotificationsOpen(false);
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Poll every 30 seconds for live updates
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [user, fetchNotifications]);

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate size (e.g., 2MB max)
    if (file.size > 2 * 1024 * 1024) {
      alert("File size must be less than 2MB");
      return;
    }

    setUploadingProfile(true);
    const formData = new FormData();
    formData.append('profilePicture', file);

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/upload-profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to upload picture');

      // Update local state
      const updatedUser = { ...user, profilePicture: data.profilePicture };
      setUser(updatedUser);
      
      // alert(data.message || 'Profile picture uploaded!');
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (err) {
      alert(err.message);
    } finally {
      setUploadingProfile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPassError('');
    setPassSuccess('');

    if (passwords.new !== passwords.confirm) {
      setPassError('New passwords do not match');
      return;
    }

    const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!regex.test(passwords.new)) {
      setPassError('Password must be at least 8 chars long, include uppercase, lowercase, number, and special symbol.');
      return;
    }

    setPassLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          email: user.emailid || user.email,
          oldPassword: passwords.old,
          newPassword: passwords.new
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');
      
      setPassSuccess(data.message || 'Password changed successfully!');
      setTimeout(() => {
        setIsPasswordModalOpen(false);
        setPasswords({ old: '', new: '', confirm: '' });
        setPassSuccess('');
      }, 2000);
    } catch (err) {
      setPassError(err.message);
    } finally {
      setPassLoading(false);
    }
  };

  const fetchMfaDevices = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/mfa/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setMfaDevices(data.devices || []);
      }
    } catch (err) {
      console.error('Error fetching trusted devices:', err);
    }
  }, []);

  const deleteMfaDevice = async (deviceId) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/mfa/devices/${deviceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (res.ok) {
        setMfaDevices(prev => prev.filter(d => d.id !== deviceId));
        setMfaSuccess('Device removed successfully.');
        setTimeout(() => setMfaSuccess(''), 2000);
      } else {
        const data = await res.json();
        throw new Error(data.error || 'Failed to remove device');
      }
    } catch (err) {
      setMfaError(err.message);
      setTimeout(() => setMfaError(''), 3000);
    }
  };

  const setupTotpMfa = async () => {
    setMfaLoading(true);
    setMfaError('');
    setMfaSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/mfa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to initialize MFA setup');

      setMfaSecret(data.secret);
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(data.otpauthUrl)}`;
      setMfaQrCode(qrUrl);
      setMfaStep('totp-setup');
    } catch (err) {
      setMfaError(err.message);
    } finally {
      setMfaLoading(false);
    }
  };

  const enableTotpMfa = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    setMfaError('');
    setMfaSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/mfa/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          secret: mfaSecret,
          code: mfaCode
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'MFA validation failed');

      setMfaSuccess('Google Authenticator MFA enabled successfully!');
      setIsMfaVerified(true);
      setUser(prev => ({ ...prev, mfa_enabled: true, mfa_type: 'totp' }));
      setTimeout(() => {
        setMfaStep('status');
        setIsMfaModalOpen(false);
        setMfaCode('');
        setMfaSuccess('');
        setIsMfaVerified(false);
      }, 2000);
    } catch (err) {
      setMfaError(err.message);
    } finally {
      setMfaLoading(false);
    }
  };

  const setupEmailMfa = async () => {
    setMfaLoading(true);
    setMfaError('');
    setMfaSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/mfa/setup-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send verification email');

      setMfaSuccess('A verification OTP has been sent to your email.');
      setMfaStep('email-setup');
    } catch (err) {
      setMfaError(err.message);
    } finally {
      setMfaLoading(false);
    }
  };

  const enableEmailMfa = async (e) => {
    e.preventDefault();
    setMfaLoading(true);
    setMfaError('');
    setMfaSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/mfa/enable-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          code: mfaCode
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'OTP verification failed');

      setMfaSuccess('Email MFA enabled successfully!');
      setIsMfaVerified(true);
      setUser(prev => ({ ...prev, mfa_enabled: true, mfa_type: 'email' }));
      setTimeout(() => {
        setMfaStep('status');
        setIsMfaModalOpen(false);
        setMfaCode('');
        setMfaSuccess('');
        setIsMfaVerified(false);
      }, 2000);
    } catch (err) {
      setMfaError(err.message);
    } finally {
      setMfaLoading(false);
    }
  };

  const handleDisableMfaStart = async () => {
    setMfaCode('');
    setMfaError('');
    setMfaSuccess('');
    if (user.mfa_type === 'email') {
      setMfaLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/mfa/setup-email`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to send verification email');

        setMfaSuccess('A verification OTP has been sent to your email.');
        setMfaStep('disable-confirm');
      } catch (err) {
        setMfaError(err.message);
      } finally {
        setMfaLoading(false);
      }
    } else {
      setMfaStep('disable-confirm');
    }
  };

  const disableMfa = async (e) => {
    if (e) e.preventDefault();
    setMfaLoading(true);
    setMfaError('');
    setMfaSuccess('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/mfa/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          code: mfaCode
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to disable MFA');

      setMfaSuccess('MFA has been disabled.');
      setIsMfaVerified(true);
      setUser(prev => ({ ...prev, mfa_enabled: false, mfa_type: 'none' }));
      setTimeout(() => {
        setMfaStep('status');
        setIsMfaModalOpen(false);
        setMfaCode('');
        setMfaSuccess('');
        setIsMfaVerified(false);
      }, 2000);
    } catch (err) {
      setMfaError(err.message);
    } finally {
      setMfaLoading(false);
    }
  };

  useEffect(() => {
    if (isMfaModalOpen && user) {
      fetchMfaDevices();
    }
  }, [isMfaModalOpen, user, fetchMfaDevices]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const handleNavClick = useCallback((id, targetId = null) => {
    if (id === 'sites') {
      window.open('https://sites.mp-xglobalsolutions.com', '_blank', 'noopener,noreferrer');
      setIsMobileMenuOpen(false);
      return;
    }
    if (id === 'manpower-sourcing') {
      window.open(`${window.location.origin}${window.location.pathname}?tab=manpower-sourcing`, '_blank');
      setIsMobileMenuOpen(false);
      return;
    }
    setActiveTab(id, targetId);
    setIsMobileMenuOpen(false);
    if (!targetId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [setActiveTab]);

  const filteredSearchItems = searchQuery
    ? SEARCH_ITEMS.filter(item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  // Handle keyboard shortcuts (Ctrl+K or Cmd+K for search, Escape to close)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsSearchModalOpen(prev => !prev);
      }
      if (e.key === 'Escape') {
        setIsSearchModalOpen(false);
        setIsLangDropdownOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-focus search input when search modal opens
  useEffect(() => {
    if (isSearchModalOpen) {
      setTimeout(() => {
        searchModalInputRef.current?.focus();
      }, 60);
    }
  }, [isSearchModalOpen]);

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(e.target)) {
        setIsSearchModalOpen(false);
      }
    };
    if (isSearchModalOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchModalOpen]);

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target)) {
        setIsLangDropdownOpen(false);
      }
    };
    if (isLangDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isLangDropdownOpen]);

  // Sync selected language from localStorage or googtrans cookie on mount
  useEffect(() => {
    const savedLang = localStorage.getItem('mpx_selected_language');
    if (savedLang) {
      setSelectedLang(savedLang);
    }
  }, []);

  const handleLangMouseEnter = () => {
    if (typeof window !== 'undefined' && window.loadGoogleTranslate) {
      window.loadGoogleTranslate();
    }
    if (langTimeoutRef.current) {
      clearTimeout(langTimeoutRef.current);
      langTimeoutRef.current = null;
    }
    setIsLangDropdownOpen(true);
  };

  const handleLangMouseLeave = () => {
    if (langTimeoutRef.current) {
      clearTimeout(langTimeoutRef.current);
    }
    langTimeoutRef.current = setTimeout(() => {
      setIsLangDropdownOpen(false);
    }, 200);
  };

  const handleSelectLanguage = (lang) => {
    if (typeof window !== 'undefined' && window.loadGoogleTranslate) {
      window.loadGoogleTranslate();
    }
    setSelectedLang(lang.code);
    localStorage.setItem('mpx_selected_language', lang.code);
    setIsLangDropdownOpen(false);

    const targetCode = lang.googleCode || lang.code;
    const hostname = window.location.hostname;

    const setCookie = (name, value) => {
      document.cookie = `${name}=${value}; path=/;`;
      document.cookie = `${name}=${value}; path=/; domain=${hostname};`;
      if (hostname.includes('.')) {
        document.cookie = `${name}=${value}; path=/; domain=.${hostname};`;
      }
    };

    const clearCookie = (name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname};`;
      if (hostname.includes('.')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname};`;
      }
    };

    if (targetCode === 'en') {
      clearCookie('googtrans');
      setLangToast('Switching to English...');
      const combo = document.querySelector('.goog-te-combo');
      if (combo) {
        combo.value = '';
        combo.dispatchEvent(new Event('change'));
      }
      setTimeout(() => {
        window.location.reload();
      }, 300);
      return;
    }

    setCookie('googtrans', `/en/${targetCode}`);
    setLangToast(`Translating to ${lang.name}...`);

    const combo = document.querySelector('.goog-te-combo');
    if (combo) {
      combo.value = targetCode;
      combo.dispatchEvent(new Event('change'));
      setTimeout(() => {
        setLangToast(`Language switched to ${lang.name}`);
        setTimeout(() => setLangToast(null), 3000);
      }, 500);
    } else {
      setTimeout(() => {
        window.location.reload();
      }, 300);
    }
  };

  const modalFilteredSearchItems = SEARCH_ITEMS.filter(item => {
    const matchesCategory = selectedSearchCategory === 'All' || item.category === selectedSearchCategory;
    const query = searchQuery.toLowerCase().trim();
    if (!query) return matchesCategory;
    const matchesQuery = item.title.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query) ||
      item.category.toLowerCase().includes(query);
    return matchesCategory && matchesQuery;
  });

  const [activeSearchIndex, setActiveSearchIndex] = useState(0);

  const handleSelectSearchItem = (item) => {
    if (!item) return;
    setIsSearchModalOpen(false);
    setSearchQuery('');
    setActiveSearchIndex(0);
    if (item.externalUrl) {
      window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    handleNavClick(item.tabId);
    if (item.elementId) {
      setTimeout(() => {
        document.getElementById(item.elementId)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const handleSearchKeyDown = (e) => {
    const results = modalFilteredSearchItems;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSearchIndex((prev) => (prev + 1) % (results.length || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSearchIndex((prev) => (prev - 1 + (results.length || 1)) % (results.length || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results.length > 0) {
        const selected = results[activeSearchIndex] || results[0];
        handleSelectSearchItem(selected);
      }
    } else if (e.key === 'Escape') {
      setIsSearchModalOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      {/* ─── ZOHO SITES STYLE THREE-TIER NAVBAR ─── */}
      <nav
        id="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled
          ? 'shadow-[0_4px_25px_rgba(0,0,0,0.08)]'
          : ''
          }`}
      >
        {/* ─── TIER 1: TOP ANNOUNCEMENT STRIP (ZOHO SITES STYLE) ─── */}
        <div className="bg-[#0b1736] text-white text-[11px] sm:text-[12px] py-1.5 px-4 text-center font-medium tracking-normal border-b border-blue-900/50">
          <span>The all-new <span className="notranslate font-semibold" translate="no">MP-X Sites</span> is here. </span>
          <a
            href="https://sites.mp-xglobalsolutions.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-white hover:text-cyan-300 transition-colors ml-1 cursor-pointer no-underline hover:no-underline focus:no-underline active:no-underline"
          >
            Discover what’s new ↗
          </a>
        </div>

        {/* ─── TIER 2: MASTER ECOSYSTEM BAR (ZOHO TOP BAR STYLE) ─── */}
        <div className="bg-white border-b border-gray-200/80">
          <div className="max-w-7xl mx-auto px-2.5 sm:px-6 flex items-center justify-between h-10 sm:h-11 gap-1.5 sm:gap-3">
            {/* Left: Sister Portal Links */}
            <div className="flex items-center gap-2.5 sm:gap-7 py-1 flex-shrink-0 overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {/* Energy Sector */}
              <button
                id="topbar-energy-sector"
                onClick={() => {
                  window.open(`${window.location.origin}${window.location.pathname}?tab=manpower-sourcing`, '_blank');
                }}
                className="group text-[12px] sm:text-[14px] font-normal text-[#000000] hover:text-[#0052cc] transition-colors flex items-center gap-1 sm:gap-1.5 whitespace-nowrap cursor-pointer flex-shrink-0 no-underline hover:no-underline focus:no-underline active:no-underline outline-none"
                style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }}
                title="Opens Energy Sector portal in a new tab"
              >
                <span className="font-normal text-[#000000] group-hover:text-[#0052cc] transition-colors no-underline">Energy Sector</span>
                <span className="text-[10px] sm:text-[11px] font-normal text-blue-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform no-underline">↗</span>
              </button>

              {/* Sites */}
              <button
                id="topbar-sites-portal"
                onClick={() => {
                  window.open('https://sites.mp-xglobalsolutions.com', '_blank', 'noopener,noreferrer');
                }}
                className="group text-[12px] sm:text-[14px] font-normal text-[#000000] hover:text-[#0052cc] transition-colors flex items-center gap-1 sm:gap-1.5 whitespace-nowrap cursor-pointer flex-shrink-0 no-underline hover:no-underline focus:no-underline active:no-underline outline-none"
                style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                }}
                title="Opens sites.mp-xglobalsolutions.com in a new tab"
              >
                <span className="font-normal text-[#000000] group-hover:text-[#0052cc] transition-colors no-underline">Sites</span>
                <span className="text-[10px] sm:text-[11px] font-normal text-blue-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform no-underline">↗</span>
              </button>
            </div>

            {/* Right: Search, English & Action Controls */}
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              {/* Search Button & Compact Dropdown under 2nd Layer */}
              <div className="relative" ref={searchDropdownRef}>
                <button
                  id="topbar-search-btn"
                  onClick={() => {
                    setIsSearchModalOpen(prev => !prev);
                    if (isSearchModalOpen) setSearchQuery('');
                  }}
                  className={`p-1.5 transition-all rounded-full cursor-pointer flex items-center justify-center ${
                    isSearchModalOpen ? 'text-red-500 hover:bg-red-50' : 'text-slate-700 hover:text-[#0052cc] hover:bg-slate-100/90'
                  }`}
                  title={isSearchModalOpen ? "Close search" : "Search"}
                >
                  {isSearchModalOpen ? (
                    <X size={18} strokeWidth={2.5} className="text-red-500 hover:text-red-600 transition-colors" />
                  ) : (
                    <Search size={16} className="text-slate-700 hover:text-[#0052cc] transition-colors" />
                  )}
                </button>

                {isSearchModalOpen && (
                  <>
                    <div className="fixed inset-0 z-40 sm:hidden bg-black/25 backdrop-blur-[1px]" onClick={() => setIsSearchModalOpen(false)} />
                    <div className="fixed sm:absolute top-[70px] sm:top-full left-2 right-2 sm:left-auto sm:right-0 pt-2 z-50 animate-fade-in w-auto sm:w-[460px]">
                      <div className="relative bg-white rounded-2xl shadow-[0_12px_45px_rgba(0,0,0,0.15)] border border-slate-200/90 p-3 sm:p-3.5">
                        {/* Pointer Triangle pointing up to Search button */}
                        <div className="hidden sm:block absolute -top-[7px] right-3 w-3.5 h-3.5 bg-white border-t border-l border-slate-200/90 rotate-45 z-20 pointer-events-none" />

                      {/* Search Form with Enter Key Support */}
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const results = modalFilteredSearchItems;
                          if (results.length > 0) {
                            handleSelectSearchItem(results[activeSearchIndex] || results[0]);
                          }
                        }}
                        className="relative w-full"
                      >
                        <input
                          ref={searchModalInputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setActiveSearchIndex(0);
                          }}
                          onKeyDown={handleSearchKeyDown}
                          placeholder="Search for product overviews, FAQs, and more..."
                          className="w-full bg-white border border-gray-200 rounded-lg px-3.5 py-2.5 pr-10 text-sm text-slate-800 placeholder:text-gray-400 focus:outline-none focus:border-[#0052cc] shadow-xs transition-colors"
                          autoFocus
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                          {searchQuery && (
                            <button
                              type="button"
                              onClick={() => {
                                setSearchQuery('');
                                setActiveSearchIndex(0);
                              }}
                              className="p-1 rounded-md text-gray-400 hover:text-gray-600 cursor-pointer"
                              title="Clear input"
                            >
                              <X size={13} />
                            </button>
                          )}
                          <Search size={15} className="text-gray-400 pointer-events-none" />
                        </div>
                      </form>

                      {/* Results list when query is typed */}
                      {searchQuery.trim() && (
                        <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-slate-100 p-0.5">
                          {modalFilteredSearchItems.length > 0 ? (
                            modalFilteredSearchItems.slice(0, 6).map((item, idx) => {
                              const isSelected = idx === activeSearchIndex;
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => handleSelectSearchItem(item)}
                                  onMouseEnter={() => setActiveSearchIndex(idx)}
                                  className={`w-full text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-2.5 group cursor-pointer ${
                                    isSelected
                                      ? 'bg-blue-50/80 text-[#0052cc]'
                                      : 'text-slate-800 hover:bg-slate-50'
                                  }`}
                                >
                                  <Search size={14} className={isSelected ? 'text-[#0052cc]' : 'text-slate-400'} />
                                  <span className="text-sm font-semibold truncate">
                                    {item.title}
                                  </span>
                                </button>
                              );
                            })
                          ) : (
                            <div className="text-center py-4 px-3">
                              <p className="text-xs font-bold text-slate-700">No results found for "{searchQuery}"</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

              {/* Language Selector Dropdown on Hover */}
              {/* Language Selector Dropdown on Hover */}
              <div
                className="relative notranslate"
                translate="no"
                ref={langDropdownRef}
                onMouseEnter={handleLangMouseEnter}
                onMouseLeave={handleLangMouseLeave}
              >
                <button
                  id="topbar-language-btn"
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.loadGoogleTranslate) {
                      window.loadGoogleTranslate();
                    }
                    setIsLangDropdownOpen(prev => !prev);
                  }}
                  className="flex items-center gap-1 sm:gap-1.5 text-[12px] sm:text-[13.5px] font-normal text-[#000000] hover:text-[#0052cc] px-1.5 sm:px-2.5 py-1 rounded-lg hover:bg-slate-100/90 transition-all cursor-pointer group notranslate"
                  style={{
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                  }}
                  translate="no"
                  title="Change Language"
                >
                  <Globe size={14} className="text-[#0052cc] shrink-0" />
                  <span className="font-semibold text-[#000000] group-hover:text-[#0052cc] uppercase notranslate tracking-wide" translate="no">
                    {(LANGUAGES.find(l => l.code === selectedLang)?.code || 'en').slice(0, 2).toUpperCase()}
                  </span>
                  <ChevronDown size={12} className={`text-slate-400 group-hover:text-[#0052cc] transition-transform duration-200 ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isLangDropdownOpen && (
                  <>
                    {/* Mobile Backdrop to close on tap outside */}
                    <div 
                      className="fixed inset-0 z-40 sm:hidden bg-black/25 backdrop-blur-[1px]" 
                      onClick={() => setIsLangDropdownOpen(false)} 
                    />
                    <div id="topbar-language-dropdown" className="fixed sm:absolute top-[70px] sm:top-full left-2 right-2 sm:left-auto sm:right-0 pt-2 z-50 animate-fade-in notranslate flex justify-center sm:block" translate="no">
                      <div className="relative bg-white rounded-2xl sm:rounded-xl shadow-[0_12px_45px_rgba(0,0,0,0.18)] border border-slate-200/90 p-3.5 sm:p-7 w-full sm:w-[500px] max-w-[500px] max-h-[calc(100vh-90px)] overflow-y-auto notranslate" translate="no">
                        {/* Pointer Triangle pointing up to English (hidden on mobile) */}
                        <div className="hidden sm:block absolute -top-[7px] right-8 sm:right-9 w-3.5 h-3.5 bg-white border-t border-l border-slate-200/90 rotate-45 z-20 pointer-events-none" />

                        {/* 2-Column Grid matching reference image */}
                        <div className="grid grid-cols-2 gap-x-2 sm:gap-x-8 gap-y-1.5 sm:gap-y-3.5 notranslate" translate="no">
                          {/* Column 1 */}
                          <div className="space-y-1 sm:space-y-3 notranslate" translate="no">
                            {LANGUAGES.slice(0, 7).map((lang) => {
                              const isSelected = selectedLang === lang.code;
                              return (
                                <button
                                  key={lang.code}
                                  onClick={() => handleSelectLanguage(lang)}
                                  className={`w-full flex items-center gap-2 sm:gap-3 py-1.5 sm:py-1 px-1 sm:px-1.5 rounded-lg text-left transition-colors cursor-pointer group notranslate ${
                                    isSelected
                                      ? 'text-blue-600 font-bold bg-blue-50/60 sm:bg-transparent'
                                      : 'text-slate-800 hover:text-blue-600 font-medium hover:bg-slate-50'
                                  }`}
                                  translate="no"
                                >
                                  <span className="w-5 h-4 shrink-0 flex items-center justify-center">
                                    {lang.useGlobe ? (
                                      <Globe size={15} className="text-slate-800 group-hover:text-blue-600 transition-colors" />
                                    ) : (
                                      <img
                                        src={`https://flagcdn.com/24x18/${lang.flagIso}.png`}
                                        srcSet={`https://flagcdn.com/48x36/${lang.flagIso}.png 2x`}
                                        width="20"
                                        height="15"
                                        alt={lang.name}
                                        className="w-5 h-3.5 object-cover rounded-[2px] shadow-[0_1px_2px_rgba(0,0,0,0.18)] border border-slate-200/90 shrink-0"
                                        loading="lazy"
                                      />
                                    )}
                                  </span>
                                  <span className="text-[12px] sm:text-[13.5px] tracking-tight truncate notranslate" translate="no">{lang.name}</span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Column 2 */}
                          <div className="space-y-1 sm:space-y-3 notranslate" translate="no">
                            {LANGUAGES.slice(7).map((lang) => {
                              const isSelected = selectedLang === lang.code;
                              return (
                                <button
                                  key={lang.code}
                                  onClick={() => handleSelectLanguage(lang)}
                                  className={`w-full flex items-center gap-2 sm:gap-3 py-1.5 sm:py-1 px-1 sm:px-1.5 rounded-lg text-left transition-colors cursor-pointer group notranslate ${
                                    isSelected
                                      ? 'text-blue-600 font-bold bg-blue-50/60 sm:bg-transparent'
                                      : 'text-slate-800 hover:text-blue-600 font-medium hover:bg-slate-50'
                                  }`}
                                  translate="no"
                                >
                                  <span className="w-5 h-4 shrink-0 flex items-center justify-center">
                                    {lang.useGlobe ? (
                                      <Globe size={15} className="text-slate-800 group-hover:text-blue-600 transition-colors" />
                                    ) : (
                                      <img
                                        src={`https://flagcdn.com/24x18/${lang.flagIso}.png`}
                                        srcSet={`https://flagcdn.com/48x36/${lang.flagIso}.png 2x`}
                                        width="20"
                                        height="15"
                                        alt={lang.name}
                                        className="w-5 h-3.5 object-cover rounded-[2px] shadow-[0_1px_2px_rgba(0,0,0,0.18)] border border-slate-200/90 shrink-0"
                                        loading="lazy"
                                      />
                                    )}
                                  </span>
                                  <span className="text-[12px] sm:text-[13.5px] tracking-tight truncate notranslate" translate="no">{lang.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {user && (
              <div className="relative">
                <button
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  className={`flex items-center justify-center w-10 h-10 rounded-full transition-all relative ${
                    isNotificationsOpen 
                    ? 'bg-blue-50 text-[#3b82f6]' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  title="Notifications"
                >
                  <Bell size={18} className={unreadCount > 0 ? 'animate-bounce' : ''} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white animate-pulse">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {isNotificationsOpen && (
                  <>
                    <div className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" onClick={() => setIsNotificationsOpen(false)}></div>
                    <div className="fixed sm:absolute top-[70px] sm:top-auto right-2 left-2 sm:right-0 sm:left-auto mt-0 sm:mt-3.5 w-auto sm:w-[410px] bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.14)] border border-slate-100 overflow-hidden z-50 animate-fade-in origin-top-right transition-all max-h-[calc(100vh-100px)] sm:max-h-[540px] flex flex-col">
                      
                      {/* === HEADER === */}
                      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-slate-50/70 flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-extrabold text-slate-800 tracking-tight">Notifications</h4>
                          {unreadCount > 0 && (
                            <span className="bg-[#3b82f6]/10 text-[#3b82f6] text-[11px] font-extrabold px-2.5 py-0.5 rounded-full border border-blue-200/50">
                              {unreadCount} New
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {unreadCount > 0 && (
                            <button
                              onClick={markAllAsRead}
                              className="text-xs font-bold text-[#3b82f6] hover:text-blue-700 transition-colors flex items-center gap-1"
                              title="Mark all as read"
                            >
                              <CheckCheck size={14} />
                              <span>Mark all read</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* === FILTER TABS BAR === */}
                      <div className="px-3 py-2 bg-slate-50/30 border-b border-slate-100 flex items-center gap-1 flex-shrink-0 overflow-x-auto">
                        {[
                          { id: 'all', label: 'All', count: notifications.length },
                          { id: 'unread', label: 'Unread', count: unreadCount },
                          { id: 'labs', label: 'Labs & VMs', count: notifications.filter(n => (n.category || '').toLowerCase() === 'lab' || (n.category || '').toLowerCase() === 'vm' || (n.title || '').toLowerCase().includes('vm') || (n.title || '').toLowerCase().includes('lab')).length },
                          { id: 'billing', label: 'Billing', count: notifications.filter(n => (n.category || '').toLowerCase() === 'billing' || (n.category || '').toLowerCase() === 'payment' || (n.title || '').toLowerCase().includes('payment') || (n.title || '').toLowerCase().includes('invoice')).length }
                        ].map(tab => (
                          <button
                            key={tab.id}
                            onClick={() => setNotificationFilter(tab.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap ${
                              notificationFilter === tab.id
                                ? 'bg-[#3b82f6] text-white shadow-sm'
                                : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900'
                            }`}
                          >
                            <span>{tab.label}</span>
                            {tab.count > 0 && (
                              <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                                notificationFilter === tab.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-700'
                              }`}>
                                {tab.count}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>

                      {/* === NOTIFICATION LIST === */}
                      <div className="overflow-y-auto divide-y divide-slate-100/70 flex-1 max-h-[320px] sm:max-h-[380px]">
                        {loadingNotifications && notifications.length === 0 ? (
                          <div className="p-8 text-center text-slate-400 text-xs">
                            <Loader2 className="animate-spin h-5 w-5 mx-auto mb-2 text-[#3b82f6]" />
                            Loading notifications...
                          </div>
                        ) : (() => {
                          const filtered = notifications.filter(notif => {
                            if (notificationFilter === 'unread') return !notif.isRead;
                            if (notificationFilter === 'labs') {
                              const cat = (notif.category || '').toLowerCase();
                              const t = (notif.title || '').toLowerCase();
                              return cat === 'lab' || cat === 'vm' || cat === 'server' || t.includes('vm') || t.includes('lab');
                            }
                            if (notificationFilter === 'billing') {
                              const cat = (notif.category || '').toLowerCase();
                              const t = (notif.title || '').toLowerCase();
                              return cat === 'billing' || cat === 'payment' || cat === 'invoice' || t.includes('payment') || t.includes('invoice') || t.includes('plan');
                            }
                            return true;
                          });

                          if (filtered.length === 0) {
                            return (
                              <div className="p-8 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
                                <Bell size={26} className="text-slate-300 stroke-[1.5]" />
                                <p className="font-bold text-slate-600">No notifications found</p>
                                <p className="text-[11px] text-slate-400 max-w-[200px] leading-relaxed">
                                  {notificationFilter === 'unread'
                                    ? 'All notifications have been read!'
                                    : 'There are no notifications in this category.'}
                                </p>
                              </div>
                            );
                          }

                          return filtered.map((notif) => {
                            const meta = getNotificationMeta(notif);
                            const IconComponent = meta.icon;
                            const target = getNotificationTarget(notif);

                            return (
                              <div
                                key={notif.id}
                                onClick={() => handleNotificationClick(notif)}
                                className={`p-3.5 transition-all duration-200 cursor-pointer flex gap-3 items-start group ${
                                  notif.isRead ? 'hover:bg-slate-50/70' : 'bg-blue-50/25 hover:bg-blue-50/50'
                                }`}
                              >
                                <div className={`p-2 rounded-xl flex-shrink-0 mt-0.5 shadow-sm transition-transform group-hover:scale-105 ${meta.iconBg}`}>
                                  <IconComponent size={16} />
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-2">
                                    <p className={`text-xs truncate ${notif.isRead ? 'font-medium text-slate-700' : 'font-bold text-slate-900'}`}>
                                      {notif.title}
                                    </p>
                                    <span className="text-[10px] font-semibold text-slate-400 whitespace-nowrap">
                                      {getRelativeTime(notif.created_at)}
                                    </span>
                                  </div>
                                  
                                  <p className="text-[11px] text-slate-500 line-clamp-2 mt-0.5 leading-relaxed">
                                    {notif.message}
                                  </p>

                                  <div className="flex items-center justify-between mt-2 gap-2">
                                    <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase tracking-wider ${meta.badgeBg}`}>
                                      {notif.category || 'General'}
                                    </span>

                                    <button
                                      type="button"
                                      onClick={(e) => handleNotificationDirectNavigate(notif, e)}
                                      className="flex items-center gap-1 text-[10px] font-bold text-[#3b82f6] opacity-0 group-hover:opacity-100 transition-opacity hover:text-blue-700 no-underline"
                                      title="Go to section"
                                    >
                                      <span>{target.label}</span>
                                      <ChevronRight size={11} />
                                    </button>
                                  </div>
                                </div>

                                <div className="flex flex-col gap-1 items-end self-start flex-shrink-0">
                                  <button
                                    onClick={(e) => deleteNotification(notif.id, e)}
                                    className="text-slate-300 hover:text-red-500 p-1 rounded-lg transition-colors hover:bg-red-50"
                                    title="Delete notification"
                                  >
                                    <X size={13} />
                                  </button>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>

                      {/* === FOOTER === */}
                      <div className="px-4 py-2.5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-[11px] font-medium text-slate-500 flex-shrink-0">
                        <span>{notifications.length} total notifications</span>
                        {notifications.some(n => n.isRead) && (
                          <button
                            onClick={clearReadNotifications}
                            className="font-bold text-slate-500 hover:text-red-600 transition-colors flex items-center gap-1"
                          >
                            <Trash2 size={12} />
                            <span>Clear Read</span>
                          </button>
                        )}
                      </div>

                    </div>
                  </>
                )}
              </div>
            )}
            
            <div className="relative mr-2">
              {user ? (
                <div className="relative">
                  <button 
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 text-white font-extrabold shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all duration-300 overflow-hidden relative border-2 border-white ring-2 ring-blue-100/70 hover:ring-blue-300/80"
                  >
                    {user.picture || user.profilePicture ? (
                      <img src={user.picture || user.profilePicture} alt="Profile" className="w-full h-full object-cover animate-fade-in" referrerPolicy="no-referrer" />
                    ) : (
                      user.username ? user.username.charAt(0).toUpperCase() : 'U'
                    )}
                  </button>
                  {purchasedPlan && (
                    <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 p-[3px] rounded-full border-2 border-white shadow-sm flex items-center justify-center z-10 animate-pulse" title="PRO Member">
                      <Crown size={11} strokeWidth={3} />
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  onClick={() => setActiveTab('auth')}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all shadow-sm hover:scale-105 active:scale-95"
                >
                  <User size={18} />
                </button>
              )}
              
              {isProfileOpen && user && (
                <>
                  <div className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" onClick={() => setIsProfileOpen(false)}></div>
                  <div className="fixed sm:absolute top-[70px] sm:top-full right-2 left-2 sm:right-0 sm:left-auto mt-0 sm:mt-3.5 w-auto sm:w-72 max-w-[320px] mx-auto bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.12)] border border-slate-100 overflow-hidden z-50 animate-fade-in origin-top-right transition-all">
                    
                    {/* Hidden File Input for Avatar Photo Upload */}
                    <input 
                      type="file" 
                      accept="image/*" 
                      ref={fileInputRef} 
                      onChange={handleProfilePictureUpload} 
                      className="hidden" 
                    />
                    
                    {/* Premium Header */}
                    <div className="px-5 py-5 border-b border-slate-100 bg-slate-50/50 flex flex-col gap-4">
                      <div className="flex items-center gap-3.5">
                        
                        {/* Interactive Avatar inside Dropdown */}
                        <div 
                          className="relative group/avatar cursor-pointer flex-shrink-0"
                          onClick={() => fileInputRef.current?.click()}
                          title="Click to change profile picture"
                        >
                          <div className="w-14 h-14 rounded-full p-[2px] bg-gradient-to-tr from-blue-600 via-indigo-500 to-purple-500 shadow-md transition-transform duration-300 group-hover/avatar:scale-105 overflow-hidden flex items-center justify-center">
                            <div className="w-full h-full rounded-full bg-white overflow-hidden flex items-center justify-center relative">
                              {user.picture || user.profilePicture ? (
                                <img src={user.picture || user.profilePicture} alt="Profile" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                              ) : (
                                <span className="text-xl font-bold text-indigo-600">
                                  {user.username ? user.username.charAt(0).toUpperCase() : 'U'}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Upload Progress/Success Overlay */}
                          {uploadingProfile ? (
                            <div className="absolute inset-0 bg-slate-900/60 rounded-full flex items-center justify-center z-10">
                              <Loader2 size={16} className="text-white animate-spin" />
                            </div>
                          ) : uploadSuccess ? (
                            <div className="absolute inset-0 bg-green-500/80 rounded-full flex items-center justify-center z-10 animate-fade-in">
                              <CheckCircle2 size={18} className="text-white" />
                            </div>
                          ) : (
                            /* Camera Hover Overlay */
                            <div className="absolute inset-0 bg-slate-900/55 rounded-full opacity-0 group-hover/avatar:opacity-100 transition-opacity duration-200 flex flex-col items-center justify-center text-white z-10">
                              <Camera size={14} className="text-white mb-0.5" />
                              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-100">Edit</span>
                            </div>
                          )}
                        </div>

                        {/* Username and Email */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h4 className="text-base font-bold text-slate-800 truncate leading-none">
                              {user.username}
                            </h4>
                            {purchasedPlan && (
                              <span className="inline-flex items-center justify-center p-[2px] bg-yellow-400 text-yellow-950 rounded-full" title="PRO Member">
                                <Crown size={10} strokeWidth={3} />
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 truncate mt-1.5">{user.email}</p>
                        </div>
                      </div>

                      {/* Premium Plan Card */}
                      {purchasedPlan && (
                        <div className="bg-gradient-to-br from-slate-900 via-slate-850 to-indigo-950 text-white rounded-xl p-3.5 shadow-md border border-slate-800/80 relative overflow-hidden group/plan">
                          {/* Background glow circle */}
                          <div className="absolute -right-6 -bottom-6 w-20 h-20 bg-indigo-50/20 rounded-full blur-xl transition-all duration-500 group-hover/plan:scale-125"></div>
                          
                          <div className="flex items-center justify-between gap-1.5 mb-2 relative z-10">
                            <span className="inline-flex items-center gap-1 text-[9px] font-extrabold text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full uppercase tracking-wider border border-amber-400/20">
                              <Crown size={10} className="fill-amber-400" /> Active Plan
                            </span>
                            <span className="text-[10px] font-semibold text-slate-400">Pro Member</span>
                          </div>
                          <p className="text-sm font-black text-white tracking-wide relative z-10 leading-snug">
                            {purchasedPlan.name}
                          </p>
                          <p className="text-[11px] text-slate-300 font-medium capitalize mt-0.5 relative z-10">
                            {purchasedPlan.selectedCycle} billing
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Navigation Menu Links */}
                    <div className="p-2 border-b border-slate-100 space-y-0.5">
                      {user.role === 'admin' && (
                        <button 
                          onClick={() => { setIsProfileOpen(false); setActiveTab('admin'); }} 
                          className="w-full text-left px-3 py-2.5 text-xs text-indigo-700 hover:bg-indigo-50 rounded-xl transition-all font-bold flex items-center gap-3 group/item"
                        >
                          <Settings size={15} className="text-indigo-500 transition-transform group-hover/item:rotate-45" /> 
                          <span className="flex-1">Admin Dashboard</span>
                          <ChevronRight size={12} className="text-indigo-300 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                        </button>
                      )}
                      
                      <button 
                        onClick={() => { 
                          setIsProfileOpen(false); 
                          if (user) {
                            setActiveTab('lab-dashboard');
                          } else {
                            setActiveTab('lab');
                          }
                        }} 
                        className="w-full text-left px-3 py-2.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-all font-semibold flex items-center gap-3 group/item"
                      >
                        <LayoutDashboard size={15} className="text-slate-400 group-hover/item:text-blue-500 transition-colors" /> 
                        <span className="flex-1">Dashboard</span>
                        <ChevronRight size={12} className="text-slate-300 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                      </button>
                      
                      <button 
                        onClick={() => { setIsProfileOpen(false); setIsPasswordModalOpen(true); }} 
                        className="w-full text-left px-3 py-2.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-all font-semibold flex items-center gap-3 group/item"
                      >
                        <Lock size={15} className="text-slate-400 group-hover/item:text-blue-500 transition-colors" /> 
                        <span className="flex-1">Change Password</span>
                        <ChevronRight size={12} className="text-slate-300 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                      </button>

                      <button 
                        onClick={() => { setIsProfileOpen(false); setMfaStep('status'); setIsMfaModalOpen(true); }} 
                        className="w-full text-left px-3 py-2.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-xl transition-all font-semibold flex items-center gap-3 group/item"
                      >
                        <ShieldCheck size={15} className="text-slate-400 group-hover/item:text-blue-500 transition-colors" /> 
                        <span className="flex-1">MFA Settings</span>
                        <ChevronRight size={12} className="text-slate-300 opacity-0 -translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                      </button>
                    </div>

                    {/* Footer / Sign Out */}
                    <div className="p-1.5 bg-slate-50/50">
                      <button 
                        onClick={() => { 
                          setIsSignoutModalOpen(true);
                        }}
                        className="w-full text-left px-3.5 py-2.5 text-xs text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all font-bold flex items-center gap-3 group/item"
                      >
                        <LogOut size={15} strokeWidth={2.5} className="text-red-400 group-hover/item:text-red-500 group-hover/item:-translate-x-0.5 transition-transform" /> 
                        <span>Sign out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

        {/* ─── TIER 3: PRODUCT NAVIGATION BAR (ZOHO SITES STYLE) ─── */}
        <div className="bg-white/95 backdrop-blur-md border-b border-slate-200/90 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 flex items-center justify-between gap-3 sm:gap-4 py-2 sm:py-2.5">
            {/* Left: Brand Logo with Subtitle */}
            <BrandLogo onClick={() => handleNavClick('home')} />

            {/* Right: Desktop Navigation Menu */}
            <div className="hidden lg:flex items-center gap-1.5 xl:gap-2.5 2xl:gap-3.5">
              {menuItems.map((item) => {
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    id={`nav-${item.id}`}
                    onClick={() => handleNavClick(item.id)}
                    className={`relative px-3 xl:px-3.5 py-1.5 text-[14px] xl:text-[14.5px] tracking-normal transition-colors duration-150 whitespace-nowrap bg-transparent cursor-pointer no-underline hover:no-underline focus:no-underline active:no-underline outline-none select-none ${
                      isActive
                        ? 'text-[#0052cc] font-semibold'
                        : 'text-[#000000] hover:text-[#0052cc] font-normal'
                    }`}
                    style={{
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
                    }}
                  >
                    <span className="no-underline">
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Mobile Hamburger Menu Toggle on Tier 3 (Clean right-hand placement next to logo) */}
            <button
              id="mobile-nav-toggle-btn"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-700 hover:text-[#0052cc] hover:bg-slate-100 rounded-xl transition-all flex items-center justify-center shrink-0 cursor-pointer focus:outline-none"
              aria-label="Toggle Navigation Menu"
            >
              {isMobileMenuOpen ? <X size={22} className="text-slate-800" /> : <Menu size={22} className="text-slate-800" />}
            </button>
          </div>
        </div>
      </nav>

      {/* === MOBILE FULL-SCREEN MENU === */}
      {/* Overlay backdrop */}
      <div
        className={`lg:hidden fixed inset-0 bg-black/40 backdrop-blur-[2px] z-[55] transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Menu Panel — Clean white fambyte style */}
      <div
        id="mobile-menu-panel"
        className={`lg:hidden fixed inset-x-0 top-0 z-[60] bg-white mobile-menu-backdrop transition-all duration-400 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-2xl flex flex-col ${isMobileMenuOpen
          ? 'translate-y-0 opacity-100 visible'
          : '-translate-y-full opacity-0 invisible'
          }`}
        style={{ maxHeight: '100dvh' }}
      >
        {/* Mobile Menu Panel Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-slate-100 bg-slate-50/70 shrink-0">
          <BrandLogo onClick={() => { handleNavClick('home'); setIsMobileMenuOpen(false); }} markWidth={34} />
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all"
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1" style={{ maxHeight: 'calc(100dvh - 65px)' }}>


          {/* Global Search Box (placed above Home) */}
          <div className="relative px-1">
            <div className="relative">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search pages, VM labs, solutions..."
                className="w-full bg-slate-50 border border-slate-200/80 rounded-2xl pl-11 pr-10 py-3 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#3b82f6] focus:bg-white transition-all text-slate-800"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-655"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {searchQuery ? (
            /* Search Results View */
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-2 mb-1">Search Results</p>
              {filteredSearchItems.length > 0 ? (
                <div className="space-y-1.5 max-h-[50dvh] overflow-y-auto pr-1">
                  {filteredSearchItems.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        if (item.externalUrl) {
                          window.open(item.externalUrl, '_blank', 'noopener,noreferrer');
                          setIsMobileMenuOpen(false);
                          setSearchQuery('');
                          return;
                        }
                        handleNavClick(item.tabId);
                        if (item.elementId) {
                          setTimeout(() => {
                            document.getElementById(item.elementId)?.scrollIntoView({ behavior: 'smooth' });
                          }, 300);
                        }
                        setSearchQuery('');
                      }}
                      className="w-full flex items-start gap-3 p-3.5 rounded-2xl hover:bg-slate-50 active:bg-slate-100 border border-slate-100/70 transition-all text-left"
                    >
                      <div className="p-2 rounded-xl bg-blue-50 text-[#3b82f6] mt-0.5">
                        <Search size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-bold text-slate-800 truncate">{item.title}</span>
                          <span className="text-[9px] font-extrabold text-[#3b82f6] bg-[#3b82f6]/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
                            {item.category}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 line-clamp-1 mt-0.5 font-medium">{item.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-405">
                  <Search size={24} className="mx-auto mb-2 text-gray-300 animate-pulse" />
                  <p className="text-xs font-bold">No results found for "{searchQuery}"</p>
                  <p className="text-[10px] text-gray-400 mt-1">Try searching for lab, plans, solutions, or careers.</p>
                </div>
              )}
            </div>
          ) : (
            /* Nav Items */
            <div className="space-y-1 stagger-children">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  id={`mobile-nav-${item.id}`}
                  onClick={() => handleNavClick(item.id)}
                  className={`w-full flex items-center justify-between p-4 rounded-2xl font-extrabold text-[15px] transition-all duration-300 animate-fade-in ${activeTab === item.id
                    ? 'bg-gradient-to-r from-[#0052cc] to-[#2563eb] text-white shadow-lg shadow-blue-500/25'
                    : 'text-slate-900 hover:text-[#0052cc] hover:bg-blue-50/60 active:bg-blue-100/50'
                    }`}
                >
                  <span>{item.label}</span>
                  <ChevronRight size={16} className={`transition-transform ${activeTab === item.id ? 'text-white' : 'text-[#0052cc]'}`} />
                </button>
              ))}

              {/* Mobile Portals & Sectors (Bottom Layer equivalent) */}
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <p className="text-[10.5px] font-extrabold text-slate-400 uppercase tracking-wider px-2">Portals & Sectors</p>
                <button
                  onClick={() => {
                    window.open(`${window.location.origin}${window.location.pathname}?tab=manpower-sourcing`, '_blank');
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-between p-3.5 rounded-2xl font-bold text-sm bg-emerald-50/70 border border-emerald-100 text-slate-800 hover:bg-emerald-100/70 transition-all text-left"
                >
                  <span className="flex items-center gap-2.5">
                    <Zap size={17} className="text-amber-500" />
                    <span className="font-extrabold">Energy Sector</span>
                  </span>
                  <span className="text-[10px] text-emerald-700 font-extrabold bg-white px-2 py-0.5 rounded-lg border border-emerald-200">
                    Portal ↗
                  </span>
                </button>

                <a
                  href="https://sites.mp-xglobalsolutions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full flex items-center justify-between p-3.5 rounded-2xl font-bold text-sm bg-blue-50/70 border border-blue-100 text-slate-800 hover:bg-blue-100/70 transition-all text-left"
                >
                  <span className="flex items-center gap-2.5">
                    <Globe size={17} className="text-blue-500" />
                    <span className="font-extrabold">Sites</span>
                  </span>
                  <span className="text-[10px] text-blue-700 font-extrabold bg-white px-2 py-0.5 rounded-lg border border-blue-200">
                    sites.mp-xglobalsolutions.com ↗
                  </span>
                </a>
              </div>
            </div>
          )}

          {/* Brand badge at bottom */}
          <div className="pt-4 flex flex-col items-center justify-center gap-1 opacity-75">
            <div className="flex items-center gap-2">
              <Building size={14} className="text-blue-600" />
              <span className="text-[10px] font-extrabold tracking-widest text-slate-600 uppercase notranslate" translate="no">MP-X Global Solutions</span>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl relative">
            <button 
              onClick={() => setIsPasswordModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Lock size={20} className="text-indigo-600" /> Change Password
            </h3>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Old Password</label>
                <input 
                  type="password" 
                  value={passwords.old} 
                  onChange={(e) => setPasswords({...passwords, old: e.target.value})} 
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">New Password</label>
                <input 
                  type="password" 
                  value={passwords.new} 
                  onChange={(e) => setPasswords({...passwords, new: e.target.value})} 
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Confirm New Password</label>
                <input 
                  type="password" 
                  value={passwords.confirm} 
                  onChange={(e) => setPasswords({...passwords, confirm: e.target.value})} 
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              
              {passError && <p className="text-xs text-red-500 font-medium">{passError}</p>}
              {passSuccess && <p className="text-xs text-green-500 font-medium">{passSuccess}</p>}
              
              <button 
                type="submit" 
                disabled={passLoading}
                className="w-full bg-[#6366f1] text-white font-bold py-3 rounded-xl hover:bg-indigo-600 transition-colors shadow-sm mt-4 disabled:opacity-70"
              >
                {passLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Sign Out Confirmation Modal */}
      {isSignoutModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-slate-100 relative text-center">
            <button 
              onClick={() => setIsSignoutModalOpen(false)}
              className="absolute top-5 right-5 text-slate-400 hover:text-slate-650 hover:bg-slate-50 p-1.5 rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
            <div className="mx-auto w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4">
              <LogOut size={22} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">
              Sign out of MP-X?
            </h3>
            <p className="text-sm text-slate-500 mb-6 leading-relaxed">
              Are you sure you want to sign out of your account? You will need to log back in to access your remote labs.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setIsSignoutModalOpen(false)}
                className="flex-1 bg-slate-100 text-slate-700 font-bold py-3 px-4 rounded-xl hover:bg-slate-200 active:scale-95 transition-all text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('token'); 
                  localStorage.removeItem('purchasedPlan');
                  setUser(null); 
                  if(setPurchasedPlan) setPurchasedPlan(null);
                  setIsProfileOpen(false); 
                  setIsSignoutModalOpen(false); 
                  setActiveTab('home'); 
                }}
                className="flex-1 bg-red-500 text-white font-bold py-3 px-4 rounded-xl hover:bg-red-650 active:scale-95 transition-all shadow-md shadow-red-500/10 text-sm"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Notification Details Modal */}
      {activeModalNotification && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-fade-in">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
            onClick={() => setActiveModalNotification(null)}
          ></div>

          {/* Modal Content Card */}
          <div className="relative bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-slate-100 z-10 space-y-5 animate-scale-up">
            {/* Top Row: Icon and Close */}
            <div className="flex justify-between items-start">
              <div className={`p-3 rounded-2xl ${
                activeModalNotification.type === 'critical' 
                ? 'bg-red-50 text-red-500' 
                : activeModalNotification.type === 'warning' 
                ? 'bg-amber-50 text-amber-500' 
                : 'bg-blue-50 text-[#3b82f6]'
              }`}>
                <Bell size={22} className="animate-pulse" />
              </div>
              <button 
                onClick={() => setActiveModalNotification(null)}
                className="text-slate-400 hover:text-slate-650 hover:bg-slate-50 p-1.5 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Title & Metadata */}
            <div className="space-y-1">
              <span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-md ${
                activeModalNotification.type === 'critical' 
                ? 'bg-red-50 text-red-650 border border-red-100' 
                : activeModalNotification.type === 'warning' 
                ? 'bg-amber-50 text-amber-650 border border-amber-100' 
                : 'bg-blue-50 text-[#3b82f6] border border-blue-100'
              }`}>
                {activeModalNotification.category || 'System'}
              </span>
              <h3 className="text-lg font-black text-slate-800 tracking-tight pt-2 leading-tight">
                {activeModalNotification.title}
              </h3>
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                Received: {new Date(activeModalNotification.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            </div>

            {/* Message Body */}
            <div className="bg-slate-50/80 rounded-2xl p-5 border border-slate-100 max-h-56 overflow-y-auto">
              <p className="text-xs font-semibold text-slate-600 leading-relaxed whitespace-pre-wrap">
                {activeModalNotification.message}
              </p>
            </div>

            {/* Actions Footer */}
            <div className="flex gap-3 justify-end pt-2">
              <button
                onClick={(e) => {
                  deleteNotification(activeModalNotification.id, e);
                  setActiveModalNotification(null);
                }}
                className="flex-1 bg-slate-50 hover:bg-red-50 hover:text-red-600 text-slate-500 font-bold py-3 px-4 rounded-xl active:scale-95 transition-all text-xs border border-slate-100"
              >
                Delete
              </button>
              <button
                onClick={() => setActiveModalNotification(null)}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 px-4 rounded-xl active:scale-95 transition-all text-xs shadow-md shadow-slate-900/10"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MFA Settings Modal */}
      {isMfaModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl relative max-h-[90vh] overflow-y-auto font-sans">
            <button 
              onClick={() => { setIsMfaModalOpen(false); setMfaStep('status'); setMfaError(''); setMfaSuccess(''); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-650 hover:bg-slate-50 p-1.5 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
            
            <h3 className="text-xl font-bold text-gray-900 mb-5 flex items-center gap-2">
              <ShieldCheck size={22} className="text-indigo-650" /> Multi-Factor Authentication
            </h3>

            {mfaStep === 'status' && (
              <div className="space-y-6">
                {mfaError && <div className="text-red-500 text-xs text-center font-bold bg-red-50 py-2.5 rounded-xl border border-red-100 animate-fade-in">{mfaError}</div>}
                {mfaSuccess && <div className="text-green-600 text-xs text-center font-bold bg-green-50 py-2.5 rounded-xl border border-green-100 animate-fade-in">{mfaSuccess}</div>}
                {/* Status Indicator */}
                <div className={`p-4 rounded-2xl border text-sm font-semibold flex items-center gap-3 ${
                  user.mfa_enabled 
                    ? 'bg-emerald-50/60 border-emerald-105 text-emerald-800' 
                    : 'bg-slate-50 border-slate-100 text-slate-650'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${user.mfa_enabled ? 'bg-emerald-500 text-white' : 'bg-slate-300 text-slate-600'}`}>
                    {user.mfa_enabled ? '✓' : '!'}
                  </div>
                  <div>
                    <p className="font-bold text-xs uppercase tracking-wider text-slate-500">MFA Status</p>
                    <p className="text-sm font-black mt-0.5">
                      {user.mfa_enabled 
                        ? `Enabled (${user.mfa_type === 'totp' ? 'Authenticator App' : 'Email OTP'})` 
                        : 'Disabled'}
                    </p>
                  </div>
                </div>

                {/* Enable / Disable Buttons */}
                {!user.mfa_enabled ? (
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">Select MFA Method</p>
                    <button
                      onClick={setupTotpMfa}
                      disabled={mfaLoading}
                      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50/40 hover:from-blue-50 hover:to-indigo-50 border border-blue-100/50 hover:border-blue-200 rounded-2xl text-left active:scale-[0.99] transition-all"
                    >
                      <div>
                        <h4 className="text-sm font-black text-slate-805">Authenticator App (Recommended)</h4>
                        <p className="text-[11px] text-slate-500 mt-1 font-semibold">Use Google Authenticator or Microsoft Authenticator for instant secure login.</p>
                      </div>
                      <ChevronRight size={16} className="text-blue-550" />
                    </button>
                    
                    <button
                      onClick={setupEmailMfa}
                      disabled={mfaLoading}
                      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50/20 hover:from-slate-50 hover:to-blue-50/40 border border-slate-200/60 hover:border-slate-300 rounded-2xl text-left active:scale-[0.99] transition-all"
                    >
                      <div>
                        <h4 className="text-sm font-black text-slate-805">Email OTP Verification</h4>
                        <p className="text-[11px] text-slate-500 mt-1 font-semibold">Receive a 6-digit verification code in your corporate email on login.</p>
                      </div>
                      <ChevronRight size={16} className="text-slate-550" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleDisableMfaStart}
                    disabled={mfaLoading}
                    className="w-full bg-red-50 text-red-650 hover:bg-red-100 hover:text-red-700 font-bold py-3.5 rounded-2xl border border-red-100/60 active:scale-95 transition-all text-sm animate-scale-up"
                  >
                    Disable Multi-Factor Authentication
                  </button>
                )}

                {/* Trusted Devices Section */}
                <div className="pt-4 border-t border-slate-100">
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 flex items-center justify-between">
                    <span>Trusted Devices ({mfaDevices.length})</span>
                    <span className="text-[10px] text-[#3b82f6] lowercase font-medium">Auto-registers verified logins</span>
                  </h4>
                  {mfaDevices.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-4 bg-slate-50 rounded-2xl border border-dashed border-slate-200">No trusted devices registered yet.</p>
                  ) : (
                    <div className="space-y-2.5 max-h-48 overflow-y-auto divide-y divide-slate-50 pr-1">
                      {mfaDevices.map(device => (
                        <div key={device.id} className="flex items-center justify-between gap-3 pt-2 text-xs font-semibold">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-xl text-slate-500">
                              <Smartphone size={16} />
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-slate-700 truncate max-w-[200px]" title={device.browser_info}>
                                {device.browser_info}
                              </p>
                              <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                                Last active: {new Date(device.last_used).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => deleteMfaDevice(device.id)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors"
                            title="Revoke device trust"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {mfaStep === 'totp-setup' && (
              <div className="space-y-5 text-center animate-fade-in">
                <p className="text-xs text-slate-500 font-medium text-left leading-relaxed">
                  1. Scan this QR Code with your Google Authenticator or Microsoft Authenticator app.
                  <br />
                  2. Enter the 6-digit confirmation code below to link the app.
                </p>

                {/* QR Code Container */}
                {mfaQrCode && (
                  <div className="bg-slate-50 p-4 rounded-2xl inline-block border border-slate-100 shadow-inner">
                    <img src={mfaQrCode} alt="MFA QR Code" className="w-48 h-48 mx-auto" />
                  </div>
                )}

                {/* Backup Secret Text */}
                <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-3 text-left">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Cannot scan? Enter code manually</p>
                  <p className="text-xs font-black font-mono tracking-widest text-slate-750 mt-1 select-all break-all text-center bg-white py-1.5 px-3 border border-slate-100 rounded-lg">
                    {mfaSecret}
                  </p>
                </div>

                <form onSubmit={enableTotpMfa} className="space-y-4 pt-2">
                  {mfaError && <div className="text-red-500 text-xs text-center font-bold bg-red-50 py-2.5 rounded-xl border border-red-100 animate-fade-in">{mfaError}</div>}
                  {mfaSuccess && <div className="text-green-600 text-xs text-center font-bold bg-green-50 py-2.5 rounded-xl border border-green-100 animate-fade-in">{mfaSuccess}</div>}
                  <input
                    type="text"
                    placeholder="Enter 6-digit Verification Code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-center text-lg font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    maxLength={6}
                    required
                  />
                  
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => { setMfaStep('status'); setMfaCode(''); setMfaError(''); }}
                      className="flex-1 bg-slate-100 text-slate-700 font-bold py-3 rounded-xl hover:bg-slate-200 active:scale-95 transition-all text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={mfaLoading || isMfaVerified}
                      className={`flex-1 font-bold py-3 rounded-xl active:scale-95 transition-all text-xs shadow-md ${
                        isMfaVerified
                          ? 'bg-emerald-600 hover:bg-emerald-600 text-white shadow-emerald-600/10'
                          : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-600/10'
                      }`}
                    >
                      {isMfaVerified ? '✓ Verified' : mfaLoading ? 'Verifying...' : 'Verify & Enable'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {mfaStep === 'email-setup' && (
              <div className="space-y-5 text-center animate-fade-in">
                <p className="text-xs text-slate-500 font-semibold text-left leading-relaxed">
                  We've sent a 6-digit OTP verification code to your email. Enter it below to enable Email MFA.
                </p>

                <form onSubmit={enableEmailMfa} className="space-y-4 pt-2">
                  {mfaError && <div className="text-red-500 text-xs text-center font-bold bg-red-50 py-2.5 rounded-xl border border-red-100 animate-fade-in">{mfaError}</div>}
                  {mfaSuccess && <div className="text-green-600 text-xs text-center font-bold bg-green-50 py-2.5 rounded-xl border border-green-100 animate-fade-in">{mfaSuccess}</div>}
                  <input
                    type="text"
                    placeholder="Enter 6-digit OTP"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-center text-lg font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    maxLength={6}
                    required
                  />
                  
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => { setMfaStep('status'); setMfaCode(''); setMfaError(''); }}
                      className="flex-1 bg-slate-100 text-slate-700 font-bold py-3 rounded-xl hover:bg-slate-200 active:scale-95 transition-all text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={mfaLoading || isMfaVerified}
                      className={`flex-1 font-bold py-3 rounded-xl active:scale-95 transition-all text-xs shadow-md ${
                        isMfaVerified
                          ? 'bg-emerald-600 hover:bg-emerald-600 text-white shadow-emerald-600/10'
                          : 'bg-indigo-650 hover:bg-indigo-600 text-white shadow-indigo-600/10'
                      }`}
                    >
                      {isMfaVerified ? '✓ Verified' : mfaLoading ? 'Verifying...' : 'Verify & Enable'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {mfaStep === 'disable-confirm' && (
              <div className="space-y-5 text-center animate-fade-in">
                <div className="mx-auto w-12 h-12 bg-red-50 text-red-500 rounded-full flex items-center justify-center mb-4">
                  <ShieldCheck size={24} className="text-red-550 animate-pulse" />
                </div>
                <h4 className="text-base font-bold text-slate-805">Disable Multi-Factor Authentication?</h4>
                <p className="text-xs text-slate-500 leading-relaxed px-2 font-semibold">
                  {user.mfa_type === 'email'
                    ? 'Enter the 6-digit OTP sent to your corporate email to confirm disabling MFA.'
                    : 'Enter the 6-digit code from your Authenticator app to confirm disabling MFA.'}
                </p>

                <form onSubmit={disableMfa} className="space-y-4 pt-2">
                  {mfaError && <div className="text-red-500 text-xs text-center font-bold bg-red-50 py-2.5 rounded-xl border border-red-100 animate-fade-in mb-3">{mfaError}</div>}
                  {mfaSuccess && <div className="text-green-600 text-xs text-center font-bold bg-green-50 py-2.5 rounded-xl border border-green-100 animate-fade-in mb-3">{mfaSuccess}</div>}

                  <input
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-center text-lg font-bold tracking-widest focus:outline-none focus:ring-2 focus:ring-red-500"
                    maxLength={6}
                    required
                  />

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => { setMfaStep('status'); setMfaCode(''); setMfaError(''); }}
                      className="flex-1 bg-slate-100 text-slate-700 font-bold py-3 rounded-xl hover:bg-slate-200 active:scale-95 transition-all text-xs"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={mfaLoading || isMfaVerified}
                      className={`flex-1 font-bold py-3 rounded-xl active:scale-95 transition-all text-xs shadow-md ${
                        isMfaVerified
                          ? 'bg-emerald-600 hover:bg-emerald-600 text-white shadow-emerald-600/10'
                          : 'bg-red-500 hover:bg-red-650 text-white shadow-red-500/10'
                      }`}
                    >
                      {isMfaVerified ? '✓ Disabled' : mfaLoading ? 'Disabling...' : 'Confirm Disable'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === NOTIFICATION DETAILS MODAL === */}
      {activeModalNotification && (() => {
        const meta = getNotificationMeta(activeModalNotification);
        const IconComponent = meta.icon;
        const target = getNotificationTarget(activeModalNotification);

        return (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in" onClick={() => setActiveModalNotification(null)}>
            <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl border border-slate-100 overflow-hidden animate-scale-up flex flex-col" onClick={(e) => e.stopPropagation()}>
              
              {/* Header */}
              <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
                <div className="flex items-center gap-3">
                  <div className={`p-2.5 rounded-2xl shadow-sm ${meta.iconBg}`}>
                    <IconComponent size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2.5 py-0.5 rounded-full uppercase tracking-wider ${meta.badgeBg}`}>
                        {activeModalNotification.category || 'General'}
                      </span>
                      <span className="text-xs text-slate-400 font-semibold">
                        {getRelativeTime(activeModalNotification.created_at)}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setActiveModalNotification(null)}
                  className="text-slate-400 hover:text-slate-700 p-2 rounded-xl hover:bg-slate-100 transition-colors"
                  title="Close modal"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <h3 className="text-lg font-black text-slate-900 leading-snug">
                  {activeModalNotification.title}
                </h3>
                
                <div className="p-4.5 bg-slate-50 rounded-2xl border border-slate-200/60 text-sm text-slate-700 leading-relaxed font-medium whitespace-pre-wrap">
                  {activeModalNotification.message}
                </div>

                <div className="text-[11px] text-slate-400 flex items-center justify-between pt-1 font-semibold">
                  <span>Created: {new Date(activeModalNotification.created_at).toLocaleString()}</span>
                  <span className="capitalize">Priority: {activeModalNotification.type || 'info'}</span>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="px-6 py-4 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-3">
                <button
                  onClick={() => setActiveModalNotification(null)}
                  className="px-5 py-2.5 rounded-xl font-bold text-xs text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 transition-all active:scale-95"
                >
                  Close
                </button>

                {target && target.tabId && (
                  <button
                    onClick={(e) => {
                      const notif = activeModalNotification;
                      setActiveModalNotification(null);
                      handleNotificationDirectNavigate(notif, e);
                    }}
                    className="px-5 py-2.5 rounded-xl font-extrabold text-xs text-white bg-[#3b82f6] hover:bg-blue-600 shadow-md shadow-blue-500/20 transition-all active:scale-95 flex items-center gap-2"
                  >
                    <span>{target.label}</span>
                    <ChevronRight size={14} />
                  </button>
                )}
              </div>

            </div>
          </div>
        );
      })()}



      {/* Language Change Toast Notification */}
      {langToast && (
        <div className="fixed bottom-6 right-6 z-[130] bg-slate-900 text-white px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-2.5 text-xs font-bold animate-fade-in border border-slate-700">
          <Globe size={16} className="text-[#3b82f6]" />
          <span>{langToast}</span>
        </div>
      )}
    </>
  );
};

export default Navbar;
